import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import React, {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AppContext } from "../../App";
import { API } from "../../api/api";
import { convertToUtc, ToFormData } from "../../hooks/Method";
import moment from "moment";
import DateRange from "./DateRange";
import DeepSearch from "./DeepSearch";
import Export from "./Export";
import { InputCheck } from "controls";
import { toast } from "react-toastify";

function DynamicTable({
  enableExport = false,
  enableDateRange = false,
  showDefaultDate = false,
  headers = [],
  setRecords,
  records = [],
  HeaderComp = <div></div>,
  ActionComp = <div></div>,
  dynamicSearch = true,
  FooterRow,
  endpointKey,
  exportEndpoint,
  enableSelect = false,
  setSelected,
  selected,
}) {
  const maxSelect = enableSelect ? 100 : 0; // Maximum number of records that can be selected at once
  const { authUser, setLoader } = useContext(AppContext);
  const [localLoading, setLocalLoading] = useState(false);
  const [pageNum, setPageNum] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchVal, setSearchVal] = useState("");
  const [totalRecord, setTotalRecord] = useState(null);
  const [maxSelectItems, setMaxSelectItems] = useState([]);

  const [sortDate, setSortDate] = useState({
    fromDate: showDefaultDate
      ? new Date(moment().startOf("month").toDate())
      : "",
    toDate: showDefaultDate ? new Date(moment().endOf("month").toDate()) : "",
  });

  const param = useMemo(
    () => ({
      pageNum,
      rowsPerPage,
      searchVal: searchVal || "",
      fromDate: sortDate.fromDate
        ? new Date(sortDate.fromDate).toLocaleString()
        : "",
      toDate: sortDate.toDate ? new Date(sortDate.toDate).toLocaleString() : "",
      today: new Date(),
    }),
    [pageNum, rowsPerPage, searchVal, sortDate]
  );

  const fetchRecords = () => {
    setLoader(true);
    setLocalLoading(true);
    API(endpointKey).post(
      ToFormData({
        ...param,
        fromDate: convertToUtc(param.fromDate),
        toDate: convertToUtc(param.toDate),
        today: convertToUtc(param.today),
        maxSelect,
      }),
      (res) => {
        if (res && res.status === 200) {
          setRecords([...res.result]);
          setTotalRecord(res.totalRecord || 0);
          setMaxSelectItems(res.maxSelectItems || []);
        } else setRecords([]);
        setLoader(false);
        setLocalLoading(false);
      }
    );
  };

  useEffect(() => {
    authUser && fetchRecords();
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, pageNum, rowsPerPage, sortDate, endpointKey]);

  useEffect(() => {
    if (searchVal.length <= 0 && authUser) {
      setPageNum(0);
      fetchRecords();
    }

    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchVal]);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPageNum(0);
  };

  const handleChangePage = (event, newPage) => {
    setPageNum(newPage);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRecords(0);
  };

  const handleRecSelect = (e, item) => {
    if (selected.items.length >= maxSelect && e.target.checked) {
      toast.error(
        "You can't select more than " + maxSelect + " items at a time."
      );
      return;
    } else if (e.target.checked) {
      setSelected((prev) => ({
        ...prev,
        items: [...prev.items, item.id],
      }));
    } else
      setSelected((prev) => ({
        ...prev,
        items: prev.items.filter((i) => i !== item.id),
      }));
  };

  const handleSelectAll = (e) => {
    var checked = e.target.checked;
    if (checked) {
      setSelected((prev) => ({
        ...prev,
        items: [...maxSelectItems],
        all: checked,
      }));
      if (totalRecord > maxSelect) {
        toast.warning(
          "You can select up to " + maxSelect + " items at a time."
        );
      }
    } else setSelected((prev) => ({ ...prev, all: checked, items: [] }));
  };

  return (
    <>
      <Box className="flex justify-between items-center flex-wrap mb-[15px] gap-2 bg-white p-[5px] rounded-[18px]">
        <Box className="flex">
          <HeaderComp />
        </Box>
        {dynamicSearch && (
          <div className="flex flex-wrap gap-2 w-full md:w-auto">
            {enableExport && <Export endpoint={exportEndpoint} value={param} />}
            {enableDateRange && <DateRange {...{ sortDate, setSortDate }} />}
            <DeepSearch {...{ searchVal, setSearchVal, handleSearch }} />
          </div>
        )}
      </Box>

      {/* Table  */}
      <Paper
        sx={{
          width: "100%",
          overflow: "hidden",
        }}
      >
        <TableContainer sx={{ maxHeight: "calc(100vh - 184px)" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {enableSelect && (
                  <TableCell>
                    <InputCheck
                      checked={selected.all}
                      onChange={handleSelectAll}
                      sx={{
                        "& .MuiButtonBase-root": { padding: "0px", ml: "10px" },
                      }}
                    />
                  </TableCell>
                )}

                {headers.map((item, i) => (
                  <TableCell
                    key={i}
                    sx={{
                      fontSize: "14px",
                      backgroundColor: "var(--white-color)",
                      color: "var(--primary-color)",
                      borderBottom: "1px solid var(--primaryMD-color)",
                      minWidth: "80px",
                      textTransform: "uppercase",
                      padding: "16px 8px 16px 16px",
                    }}
                  >
                    {item.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!localLoading &&
                records &&
                records.map((item, i) => {
                  return (
                    <TableRow hover key={i}>
                      {/* ------- Checkbox for Select -------  */}
                      {enableSelect && (
                        <TableCell>
                          <InputCheck
                            checked={selected.items?.some((s) => s === item.id)}
                            name={`${item.id}${item.label}`}
                            onChange={(e) => handleRecSelect(e, item)}
                            sx={{
                              "& .MuiButtonBase-root": {
                                padding: "0px",
                                ml: "10px",
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      {headers.map((headItem, index) => {
                        return (
                          <Fragment key={index}>
                            {/* ------- Table Data -------  */}
                            <TableCell
                              key={index}
                              sx={{
                                fontSize: "16px",
                                padding: "6px 16px 6px 16px",
                                whiteSpace: "nowrap",
                                color: "var(--gray-color)",
                              }}
                            >
                              {headItem.id === "actions" ? (
                                <ActionComp {...item} />
                              ) : (
                                item[headItem.id]
                              )}
                            </TableCell>
                          </Fragment>
                        );
                      })}
                    </TableRow>
                  );
                })}

              {FooterRow && <TableRow>{<FooterRow />}</TableRow>}
            </TableBody>
          </Table>

          {localLoading && (
            <Typography
              variant="h6"
              className="tx-center p-30 tx-upp gray-color"
            >
              Loading...
            </Typography>
          )}

          {!totalRecord && !localLoading && (
            <Typography
              variant="h6"
              className="tx-center p-30 tx-upp gray-color"
            >
              Data not avaiable
            </Typography>
          )}
        </TableContainer>
        {totalRecord > 0 && !localLoading && (
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            labelRowsPerPage="Row"
            count={totalRecord}
            rowsPerPage={rowsPerPage}
            page={pageNum}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>
    </>
  );
}

export default DynamicTable;
